import {
  useMutation,
  useQueryClient,
  type InfiniteData,
} from "@tanstack/react-query";
import { useAuthenticatedFetch } from "@/hooks/use-authenticated-fetch";
import { collectionKeys } from "@/lib/query-keys";
import type { PaginatedResponse, ReportCollection } from "@/types/api";
import type {
  GenerateReportRecord,
  ReportFormat,
  ReportOrientation,
  ReportTemplate,
} from "./use-generate-report";

/** A photos report, from [[report-generation-dialog.spec]]. */
export interface CreateRecordsCollectionPayload {
  kind?: "records";
  records: GenerateReportRecord[];
  name: string;
  template: ReportTemplate;
  /** Omitted when format is PPTX — PPTX is always landscape. */
  orientation?: ReportOrientation;
  format: ReportFormat;
  override_remarks?: boolean;
}

/**
 * An issues report, from [[issues-report-dialog.spec]]. No template — an issues
 * report has one layout — no order, since it is always by plan, no remarks, and
 * no history scope: every issues export shows each issue's latest status.
 */
export interface CreateIssuesCollectionPayload {
  kind: "issues";
  issueIds: string[];
  name: string;
  /** Omitted when format is PPTX — PPTX is always landscape. */
  orientation?: ReportOrientation;
  format: ReportFormat;
}

export type CreateCollectionPayload =
  | CreateRecordsCollectionPayload
  | CreateIssuesCollectionPayload;

/**
 * The request body. Format is lowercase on the wire, orientation UPPERCASE —
 * DRF ChoiceField over TextChoices with no normalization. Orientation is
 * omitted for PPTX so the backend does not have to reject a value it would
 * ignore.
 */
function requestBody(payload: CreateCollectionPayload) {
  const orientation =
    payload.format === "PDF" && payload.orientation
      ? { orientation: payload.orientation }
      : {};

  if (payload.kind === "issues") {
    return {
      kind: "issues" as const,
      name: payload.name,
      items: payload.issueIds,
      format: payload.format.toLowerCase(),
      ...orientation,
    };
  }

  const record_remarks: Record<string, string> = {};
  for (const record of payload.records) {
    if (record.remark !== undefined) record_remarks[record.id] = record.remark;
  }
  return {
    kind: "records" as const,
    name: payload.name,
    items: payload.records.map((record) => record.id),
    template: payload.template,
    format: payload.format.toLowerCase(),
    ...orientation,
    override_remarks: payload.override_remarks,
    ...(Object.keys(record_remarks).length > 0 ? { record_remarks } : {}),
  };
}

type CollectionListCache = InfiniteData<PaginatedResponse<ReportCollection>>;

/**
 * Export a selection, which is also how a report comes into existence —
 * photos from the photos dialog, issues from the issues dialog.
 *
 * There is no such thing as an export without a collection: this endpoint
 * creates the collection *and* its first export in one call, and it always
 * creates a **new** one. It never asks which collection to add to — topping up
 * an existing report happens on that report's own page, so the flow the user
 * already knows gains no new decision. See [[report-collections.spec]].
 *
 * The backend answers 202 with the collection and its PENDING export, so the
 * new row is prepended optimistically and the reports list lands populated
 * rather than flashing empty.
 */
export function useCreateCollection(projectId: string) {
  const fetchWithAuth = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CreateCollectionPayload) =>
      fetchWithAuth<ReportCollection>(
        `/reports/3.0/projects/${projectId}/collections/`,
        { method: "POST", body: JSON.stringify(requestBody(payload)) },
      ),
    onSuccess: (collection) => {
      queryClient.setQueryData<CollectionListCache>(
        collectionKeys.list(projectId),
        (oldData) => {
          if (!oldData || oldData.pages.length === 0) {
            return {
              pages: [
                { count: 1, next: null, previous: null, results: [collection] },
              ],
              pageParams: [1],
            };
          }
          const [firstPage, ...rest] = oldData.pages;
          return {
            ...oldData,
            pages: [
              {
                ...firstPage,
                count: firstPage.count + 1,
                results: [collection, ...firstPage.results],
              },
              ...rest,
            ],
          };
        },
      );

      queryClient.setQueryData(
        collectionKeys.detail(projectId, collection.id),
        collection,
      );

      void queryClient.invalidateQueries({
        queryKey: collectionKeys.list(projectId),
      });
    },
  });
}
